/**
 * Matcha inspiration — card video autoplay/loop + mobile carousel fallback.
 */
(function () {
  const MOBILE_MQ = '(max-width: 749px)';
  const SCROLL_SPEED = 22;

  function playVideo(video) {
    if (!(video instanceof HTMLVideoElement)) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('loop', '');
    video.setAttribute('playsinline', '');

    if (video.ended) {
      video.currentTime = 0;
    }

    const promise = video.play();
    if (promise && typeof promise.catch === 'function') {
      promise.catch(() => {});
    }
  }

  function bindCardVideo(video) {
    if (!(video instanceof HTMLVideoElement)) return;
    if (video.dataset.inspirationBound === 'true') return;

    video.dataset.inspirationBound = 'true';

    const ensure = () => playVideo(video);

    video.addEventListener('loadeddata', ensure);
    video.addEventListener('canplay', ensure);
    video.addEventListener('ended', ensure);

    const card = video.closest('.matcha-inspiration__card');
    if (card) {
      card.addEventListener('mouseenter', ensure);
      card.addEventListener('focusin', ensure);
    }

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) ensure();
          });
        },
        { threshold: 0.05 }
      );
      observer.observe(video);
      video._inspirationObserver = observer;
    }

    ensure();
    window.setTimeout(ensure, 250);
    window.setTimeout(ensure, 1000);
  }

  function resetCardVideos(root) {
    root.querySelectorAll('.matcha-inspiration__video').forEach((video) => {
      video.dataset.inspirationBound = 'false';
      if (video._inspirationObserver) {
        video._inspirationObserver.disconnect();
        video._inspirationObserver = null;
      }
    });
  }

  function initCardVideos(root) {
    root.querySelectorAll('.matcha-inspiration__video').forEach(bindCardVideo);
  }

  function scheduleCardVideoInit(root) {
    initCardVideos(root);
    window.requestAnimationFrame(() => initCardVideos(root));
    window.setTimeout(() => initCardVideos(root), 300);
  }

  function getCenterOffset(scroll, slot) {
    return slot.offsetLeft - (scroll.clientWidth - slot.offsetWidth) / 2;
  }

  function teardown(root) {
    const runner = root.querySelector('[data-inspiration-runner]');
    if (!runner) return;

    if (runner._inspirationRaf) {
      cancelAnimationFrame(runner._inspirationRaf);
      runner._inspirationRaf = null;
    }

    runner.style.removeProperty('--inspiration-start');
    runner.style.animation = '';
    runner.style.transform = '';
    runner.dataset.inspirationReady = 'false';
  }

  function initInspiration(root) {
    const scroll = root.querySelector('[data-inspiration-scroll]');
    const runner = root.querySelector('[data-inspiration-runner]');
    const mq = window.matchMedia(MOBILE_MQ);

    if (!scroll || !runner) return;

    if (!mq.matches) {
      teardown(root);
      return;
    }

    if (runner.dataset.inspirationReady === 'true') return;

    const firstTrack = runner.querySelector('[data-inspiration-track]');
    const slots = firstTrack ? [...firstTrack.querySelectorAll('.matcha-inspiration__slot')] : [];
    if (slots.length === 0) return;

    const centerIndex = Math.floor(slots.length / 2);
    const centerSlot = slots[centerIndex];
    const centerOffset = getCenterOffset(scroll, centerSlot);
    const startOffset = `${-centerOffset}px`;

    runner.style.setProperty('--inspiration-start', startOffset);
    runner.dataset.inspirationReady = 'true';

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) {
      runner.style.animation = 'none';
      runner.style.transform = `translate3d(${startOffset}, 0, 0)`;
      return;
    }

    runner.style.animation = 'none';
    void runner.offsetHeight;
    runner.style.removeProperty('animation');

    const cssRunning = runner.getAnimations().some((animation) => animation.playState === 'running');
    if (cssRunning) return;

    runner.style.transform = `translate3d(${startOffset}, 0, 0)`;

    let offset = centerOffset;
    let lastTime = performance.now();

    function getSetWidth() {
      return runner.scrollWidth / 2;
    }

    function tick(now) {
      if (!mq.matches || runner.dataset.inspirationReady !== 'true') return;

      const delta = (now - lastTime) / 1000;
      lastTime = now;
      offset += SCROLL_SPEED * delta;

      const setWidth = getSetWidth();
      if (setWidth > 0 && offset >= setWidth) {
        offset -= setWidth;
      }

      runner.style.transform = `translate3d(${-offset}px, 0, 0)`;
      runner._inspirationRaf = requestAnimationFrame(tick);
    }

    runner._inspirationRaf = requestAnimationFrame(tick);
  }

  function initAll() {
    document.querySelectorAll('[data-matcha-inspiration]').forEach((root) => {
      scheduleCardVideoInit(root);
      requestAnimationFrame(() => initInspiration(root));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  window.addEventListener('resize', () => {
    document.querySelectorAll('[data-matcha-inspiration]').forEach((root) => {
      const runner = root.querySelector('[data-inspiration-runner]');
      if (!runner) return;

      if (!window.matchMedia(MOBILE_MQ).matches) {
        teardown(root);
        return;
      }

      teardown(root);
      requestAnimationFrame(() => initInspiration(root));
    });
  });

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.querySelector('[data-matcha-inspiration]');
    if (root) {
      resetCardVideos(root);
      scheduleCardVideoInit(root);
      teardown(root);
      requestAnimationFrame(() => initInspiration(root));
    }
  });

  document.addEventListener('shopify:block:select', (event) => {
    const root = event.target.closest('[data-matcha-inspiration]');
    if (!root) return;
    resetCardVideos(root);
    scheduleCardVideoInit(root);
  });
})();
